/**
 * @file draft-products.tsx
 * @module app/modals/draft-products
 * @description Renders the draft products modal dialog for confirming actions, capturing workflow input, and reporting outcomes to its parent screen.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from "react";
import { Modal, Button, Card, ListGroup } from "react-bootstrap";
import CrossSvg from "../../_admin/assets/media/close.png";
import p1 from "../../_admin/assets/media/product/default.svg";
import crossRed from "../../_admin/assets/media/svg_uTrade/cross-red.svg";
import RemoveDraftProduct from "./remove-draft-product";
import APICallService from "../../api/apiCallService";
import { product } from "../../api/apiEndPoints";
import Loader from "../../Global/loader";
import Method from "../../utils/methods";
import { useNavigate } from "react-router-dom";
import { Add, Delete, MasterProduct } from "../../utils/constants";
import { useAuth } from "../modules/auth";
import { getKey } from "../../Global/history";
import { warehouse as warehouseStoreString } from "../../utils/storeString";
import { useWarehouse } from "../context/WarehouseContext";
import ImageWithFallback from "../modules/_common/components/image-with-fallback";
const DraftProducts = (props: any) => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [fetchLoader, setFetchLoader] = useState(false);
  const [draftProducts, setDraftProducts] = useState<any>([]);
  useEffect(() => {
    (async () => {
      if (props.show) {
        setFetchLoader(true);
        await fetchDraftProducts();
        setFetchLoader(false);
      }
    })();
  }, [props.show]);
  const fetchDraftProducts = async () => {
    let params: any = {
      sortKey: "title",
      sortOrder: -1,
    };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value || null;
    }
    let apiService = new APICallService(
      product.draftList,
      params,
      "",
      "",
      "",
      "",
      props.moduleName,
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    if (response.records) {
      setDraftProducts(response.records);
    }
  };
  return (
    <>
      <RemoveDraftProduct
        show={props.showRemoveDraftProduct}
        onHide={() => {
          props.handleHideRemoveDraftProduct();
        }}
        deleteId={props.deleteId}
        title={props.title}
        handleDraftCount={props.handleDraftCount}
        moduleName={props.moduleName}
      />
      <Modal
        {...props}
        show={props.show}
        onHide={props.onHide}
        dialogClassName="modal-dialog-centered min-w-lg-690px"
        className="border-r10px"
        centered
        backdrop="static"
      >
        <Modal.Header className="border-bottom-0 pb-0 text-center mx-auto">
          <img
            className="close-inner-top"
            width={40}
            height={40}
            src={CrossSvg}
            alt="closebutton"
            onClick={props.onHide}
          />
          <Modal.Title className="fs-26 fw-bolder mw-lg-375px pt-lg-3">
            Draft products
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-6">
          <Card className="pb-6">
            {fetchLoader ? (
              <div className="d-flex flex-wrap justify-content-center align-items-center p-2 py-3">
                <div className="d-md-flex">
                  <Loader loading={fetchLoader} />
                </div>
              </div>
            ) : (
              <>
                {" "}
                <div className="mx-5">
                  {draftProducts.map((productVal: any, index: number) => {
                    return (
                      <ListGroup.Item>
                        <div className="d-flex flex-wrap justify-content-between align-items-center p-2 py-3">
                          <div className="d-md-flex">
                            <div className="symbol symbol-50px border me-5">
                              {productVal.variants.length > 0 &&
                              productVal.variants[0]["variant"]["media"]
                                .length ? (
                                <ImageWithFallback
                                  src={
                                    productVal.variants[0]["variant"][
                                      "media"
                                    ][0].url
                                  }
                                  className="object-fit-contain"
                                  alt=""
                                  noDefaultSize
                                />
                              ) : (
                                <>
                                  <img src={p1} width={14} height={14} alt="" />
                                </>
                              )}
                            </div>
                            <div className="d-flex justify-content-start flex-column">
                              <span className="fs-18 fw-500 text-dark">
                                {productVal.title}
                              </span>
                              <span className="d-block fs-15 fw-500 text-gray">
                                Drafted on{" "}
                                {Method.convertDateToDDMMYYYY(
                                  productVal.draftedAt
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="d-flex justify-content-end align-items-center flex-row mt-lg-0 mt-5">
                            {Method.hasPermission(
                              MasterProduct,
                              Add,
                              currentUser
                            ) ? (
                              <Button
                                type="button"
                                variant="primary"
                                className="uw-btn uw-btn-primary uw-btn-sm fs-14 fw-600 btn-sm min-h-40px me-1"
                                onClick={() => {
                                  navigate("/products/edit-product", {
                                    state: {
                                      _id: productVal._id,
                                      isDraft: true,
                                      moduleName: MasterProduct,
                                    },
                                  });
                                }}
                              >
                                Continue adding details
                              </Button>
                            ) : (
                              <></>
                            )}
                            {Method.hasPermission(
                              MasterProduct,
                              Delete,
                              currentUser
                            ) ? (
                              <button
                                type="button"
                                className="uw-btn uw-btn-icon uw-btn-sm btn btn-icon btn-sm"
                                onClick={() =>
                                  props.handleShowRemoveDraftProduct(
                                    productVal._id,
                                    productVal.title
                                  )
                                }
                              >
                                <img
                                  src={crossRed}
                                  width={16}
                                  height={16}
                                  alt=""
                                />
                              </button>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                        {index !== draftProducts.length - 1 ? (
                          <div className="separator my-2"></div>
                        ) : (
                          <></>
                        )}{" "}
                      </ListGroup.Item>
                    );
                  })}
                </div>
              </>
            )}
          </Card>
        </Modal.Body>
      </Modal>
    </>
  );
};
export default DraftProducts;
